<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

// Tab titles, centralized: pages are keep-alive'd, so per-page useHead calls
// linger after navigation — deriving from the active route avoids that.
const route = useRoute();
const pageTitle = computed<string | null>(() => {
  const p = route.params as Record<string, string>;
  // Bare repo name is enough for a tab ("BSData~wh40k-10e" / "wh40k-10e" -> "wh40k-10e")
  const sys = p.system ? String(p.system).split(/[~/]/).pop() : "";
  switch (String(route.name || "")) {
    case "login": return "Log in";
    case "mod": return "Moderation";
    case "systems-lang": return "Systems";
    case "translate-system": return `${sys} · Languages`;
    case "translate-system-lang": return `${sys} · ${p.lang}`;
    case "translate-system-lang-review": return `Review · ${sys} ${p.lang}`;
    case "translate-system-lang-scripts": return `Scripts · ${sys} ${p.lang}`;
    case "translate-system-lang-catalogue-catalogue":
      return `${decodeURIComponent(p.catalogue || "")} · ${sys} ${p.lang}`;
    default: return null;
  }
});

useHead({
  title: pageTitle,
  titleTemplate: (t) => (t ? `${t} — NR Translation` : "New Recruit - Translation"),
});
</script>

<template>
  <div id="popups" />
  <div id="dialogs" />
  <div id="app">
    <Notifications position="top center" />
    <ClientOnly>
      <div class="content">
        <NuxtPage :keepalive="true" />
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/vars.scss" as *;

#app {
  padding: 0 !important;
  height: 100%;
  width: 100%;
  position: fixed;
}

.content {
  height: 100vh;
  width: 100%;
  overflow-y: auto;
}

html {
  font-family: sans-serif;
  background-image: linear-gradient(
    rgba(var(--bg-r), var(--bg-g), var(--bg-b), var(--bg-a)),
    rgba(var(--bg-r), var(--bg-g), var(--bg-b), var(--bg-a))
  );
  background-size: var(--backgroundSize);
  background-color: rgb(var(--bg-r), var(--bg-g), var(--bg-b));
  filter: var(--global-filter);
  color: var(--font-color);
}

img.icon,
svg.icon,
img.imgBt {
  filter: var(--image-filter);
}

input:hover,
button:hover {
  border-color: $input_highlights !important;
  filter: brightness(110%);
}

select:hover {
  border-color: $input_highlights;
}

select,
input,
button,
textarea {
  border-radius: var(--input-radius);
  background-color: var(--input-background);
  color: var(--font-color);
}

body {
  font-family: $font;
  font-size: $fontSize;
}

a {
  color: $blue;
}

.warning {
  color: $orange;
  font-weight: bold;
}

.error {
  color: $red;
  font-weight: bold;
}

select,
input,
a {
  font-family: $fontButton !important;
  font-size: $fontButtonSize !important;
}

.red {
  color: $red;
}

.green {
  color: $green;
}

.blue {
  color: $blue;
}

.grey,
.gray {
  color: $gray;
}

html {
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 5px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.4);
  }

  /* Handle on mouse */
  ::-webkit-scrollbar-thumb:active {
    background: rgba(0, 0, 0, 0.5);
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
}

html.dark {
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 5px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  /* Handle on mouse */
  ::-webkit-scrollbar-thumb:active {
    background: rgba(255, 255, 255, 0.4);
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
}

fieldset {
  border: 1px solid $box_border;
}
</style>
